import React, { useEffect } from "react";
import AOS from "aos";
import "./CloudDevOps.css";
import SEO from "../../SEO";

const cloudServices = [
  {
    title: "Cloud Infrastructure & Deployment",
    description:
      "MZR Software designs and manages secure, scalable cloud infrastructure using Microsoft Azure and modern DevOps practices.",
    features: [
      "Microsoft Azure",
      "Virtual Machines & App Services",
      "Azure SQL & Storage",
      "Load Balancing",
      "Auto Scaling",
      "Cloud Security Configuration",
    ],
    img: process.env.PUBLIC_URL + "/img/Cloud_Infrastructure_Deployment.gif",
    icon: "bi-cloud",
  },
  {
    title: "CI/CD Pipeline Automation",
    description:
      "Continuous Integration & Deployment pipelines using Azure DevOps and GitHub Actions for faster releases.",
    features: [
      "Azure DevOps",
      "GitHub Actions",
      "Automated Builds",
      "Automated Testing",
      "Continuous Deployment",
      "Git Version Control",
    ],
    img: process.env.PUBLIC_URL + "/img/cicd.gif",
    icon: "bi-git",
  },
  {
    title: "Containerization & Microservices",
    description:
      "Docker-based microservices architecture for scalable enterprise applications.",
    features: [
      "Docker",
      "Microservices Architecture",
      "API Gateway",
      "Service Isolation",
      "Scalable Deployment",
    ],
    img: process.env.PUBLIC_URL + "/img/docker.gif",
    icon: "bi-box",
  },
  {
    title: "Monitoring & Performance Optimization",
    description:
      "Real-time monitoring, logging, and performance optimization for enterprise systems.",
    features: [
      "Azure Monitor",
      "Application Insights",
      "Log Management",
      "Performance Tracking",
      "Security Alerts",
    ],
    img: process.env.PUBLIC_URL + "/img/monitoring.gif",
    icon: "bi-activity",
  },
];

const benefits = [
  "High Performance Cloud Systems",
  "Reduced Infrastructure Cost",
  "Zero Downtime Deployment",
  "Scalable Architecture",
  "Enterprise Security",
  "Faster Software Delivery",
];

const industries = [
  "ERP Systems",
  "POS Software",
  "Pharmacy Management",
  "Retail & Supermarkets",
  "E-Commerce Platforms",
  "Logistics Systems",
  "Banking Applications",
  "Healthcare Systems",
];

const faqs = [
  {
    q: "What is Cloud & DevOps?",
    a: "Cloud & DevOps combines infrastructure and automation to deploy software faster and more reliably.",
  },
  {
    q: "Do you use Azure or AWS?",
    a: "We mainly work with Microsoft Azure and modern DevOps tools.",
  },
  {
    q: "Is it suitable for ERP systems?",
    a: "Yes, ERP and POS systems perform best with cloud-based architecture.",
  },
  {
    q: "Do you provide deployment support?",
    a: "Yes, we provide full deployment and monitoring support.",
  },
];

function CloudDevOps() {
  useEffect(() => {
    AOS.init({ duration: 800, offset: 100 });
  }, []);

  return (
    <>
      <SEO
        title="Cloud & DevOps Services – MZR Software"
        description="Scalable Cloud, DevOps and automation solutions for ERP, POS and enterprise systems."
        image="https://mzrsoftware.com/assets/images/og-clouddevops.jpg"
        url="https://mzrsoftware.com/OurServices/CloudDevOps/clouddevops"
      />

      <div className="cloud-devops-page">
        {/* HERO (H1) */}
        <section className="text-center mb-5" data-aos="fade-up">
          <h1 className="section-title">
            Cloud & DevOps <span className="highlight">Solutions</span>
          </h1>
          <p className="section-subtitle">
            Secure, scalable ERP, POS and enterprise systems powered by cloud
            infrastructure, automation and CI/CD pipelines.
          </p>
        </section>

        {/* INTRODUCTION */}
        <section className="text-center mb-5" data-aos="fade-up">
          <h2>Introduction</h2>
          <p className="section-subtitle">
            We design modern cloud systems using Azure, Docker and DevOps
            automation to help businesses scale faster and reduce cost.
          </p>
        </section>

        {/* FEATURES (UNCHANGED STRUCTURE) */}
        <section className="services-section">
          <div className="container">
            <div className="section-header" data-aos="fade-up">
              <h2 className="section-title">
                MZR <span className="highlight">Cloud & DevOps Solutions</span>
              </h2>
              <p className="section-subtitle">
                Secure, scalable, and automated infrastructure management for
                enterprise systems.
              </p>
            </div>

            <div className="services-grid">
              {cloudServices.map((service, index) => (
                <div
                  key={index}
                  className={`service-card zig-zag ${
                    index % 2 === 0 ? "left" : "right"
                  }`}
                  data-aos="fade-up"
                >
                  <div className="card-content">
                    <span className={`bi ${service.icon} service-icon`}></span>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>

                    <div className="service-features">
                      {service.features.map((feature, i) => (
                        <span key={i} className="feature-tag">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="card-image">
                    <img src={service.img} alt={service.title} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="mb-5 text-center" data-aos="fade-up">
          <h2>Benefits</h2>
          <div className="portfolio-grid">
            {benefits.map((b, i) => (
              <div key={i} className="portfolio-card">
                ✔ {b}
              </div>
            ))}
          </div>
        </section>

        {/* SCREENSHOTS */}
        <section className="mb-5 text-center" data-aos="fade-up">
          <h2>Screenshots</h2>
          <div className="portfolio-grid">
            <div className="portfolio-card">Cloud Dashboard</div>
            <div className="portfolio-card">CI/CD Pipeline</div>
            <div className="portfolio-card">Monitoring System</div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="mb-5 text-center" data-aos="fade-up">
          <h2>Industries</h2>
          <div className="portfolio-grid">
            {industries.map((ind, i) => (
              <div key={i} className="portfolio-card">
                {ind}
              </div>
            ))}
          </div>
        </section>

        {/* FAQS */}
        <section className="mb-5 text-center" data-aos="fade-up">
          <h2>FAQs</h2>
          <div className="portfolio-grid">
            {faqs.map((f, i) => (
              <div key={i} className="portfolio-card">
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="portfolio-cta text-center mt-5" data-aos="fade-up">
          <h2>Upgrade Your System with Cloud & DevOps</h2>
          <p className="cta-text">
            Deploy scalable ERP, POS and enterprise systems with modern cloud
            architecture.
          </p>
          <a href="/contact" className="btn-primary">
            Get Free Consultation →
          </a>
        </section>
      </div>
    </>
  );
}

export default CloudDevOps;
